<template>
	<errorContainer :error="erreur" :warning="warning">
		<div class="animated fadeIn">
			<div class="form">
				<div v-if="$screen.width <= 576" class="my-expense-mobile">
					<div class="title">
						<h1 class="main-page-title" :class="{ 'is-pwa': $isPwa() }">
							{{ FormMSG(1, 'My invoices') }}
						</h1>
					</div>
					<div class="my-expense-actions" :class="{ 'pt-16': $isPwa() }">
						<div class="d-flex justify-content-start title-add mt-2">{{ FormMSG(56, 'Add Invoice') }}</div>
						<div class="d-flex justify-content-center wapper">
							<div class="btn-action text-center" @click="handleNewInvoice">
								<!-- eslint-disable-next-line vue/html-self-closing -->
								<img src="svgs/invoice.svg" alt="new Invoice" />
								<span class="px-2">{{ FormMSG(2, 'New Invoice') }}</span>
							</div>
						</div>
					</div>
				</div>
				<b-row v-if="$screen.width >= 576" align-h="between">
					<b-col sm="4">
						<h1>{{ FormMSG(1, 'Invoices') }}</h1>
					</b-col>
					<b-col sm="8" class="text-right">
						<b-button size="md" variant="success" class="mr-2" @click="handleNewInvoice">
							<component :is="getLucideIcon('Plus')" :size="20" class="mr-2" />
							<span>{{ FormMSG(2, 'New Invoice') }}</span>
						</b-button>
						<b-button
							v-if="showApprovalsBtn"
							size="md"
							variant="light"
							class="text-light"
							:class="!activeApproveAction ? 'bgc-slate-light' : 'btn-custom-blue-dark'"
							:disabled="!activeApproveAction"
							@click="approveSelected(selectedInvoice)"
						>
							<component :is="getLucideIcon('Check')" :size="20" class="mr-2" />
							<span>{{ FormMSG(3, 'Approve selected') }}</span>
						</b-button>
						<b-button
							v-if="showSendValidationBtn"
							size="md"
							variant="light"
							class="text-light"
							:class="!activeDraftAction || selectedInvoiceHasRefusedItem ? 'bgc-slate-light' : 'btn-custom-blue-dark'"
							:disabled="!activeDraftAction || selectedInvoiceHasRefusedItem"
							@click="sendForApproval(selectedInvoice)"
						>
							<component :is="getLucideIcon('Send')" :size="20" class="mr-2" />
							<span>{{ FormMSG(8, 'Send for approval') }}</span>
						</b-button>
					</b-col>
				</b-row>
				<b-row>
					<div class="d-flex flex-column w-100">
						<b-card no-body :class="`${$screen.width <= 576 ? 'my-project' : ''}`">
							<b-tabs v-model="tabs.activeTabs" @activate-tab="(n, p, e) => onTabClicked(n)">
								<b-tab v-if="canSeeApprove" lazy :title="FormMSG(4, 'Approve')">
									<div class="pr-1 pl-1 w-100">
										<invoices-data-view
											ref="approveDataView"
											for-approve
											:can-approve="canSeeApprove"
											:loading-review-mode="isReviewModeLoading"
											submitted-state
											@selection:change="(n, e) => invoiceSelectionChange(n, e)"
											@open:details="showInvoiceDetailsToApprove"
											@approveInvoice="(e) => approveSelected(e)"
											@sendInvoiceForApproval="(e) => sendForApproval(e)"
											@open:review-mode="(e) => openReviewMode(e, (newProcess = false))"
										/>
									</div>
								</b-tab>
								<b-tab lazy :title="FormMSG(5, 'Drafts')">
									<div class="pr-1 pl-1 w-100">
										<invoices-data-view
											ref="draft-data-view"
											:key="draftDataKey"
											for-draft
											:can-approve="canSeeApprove"
											not-submitted-state
											@selection:change="(n, e) => invoiceSelectionChange(n, e)"
											@open:details="(e) => showInvoiceDetails(e, true)"
											@approveInvoice="(e) => approveSelected(e)"
											@sendInvoiceForApproval="(e) => sendForApproval(e)"
										/>
									</div>
								</b-tab>
								<b-tab lazy :title="FormMSG(6, 'Pending')">
									<div class="pr-1 pl-1 w-100">
										<invoices-data-view
											ref="pending-data-view"
											:can-approve="canSeeApprove"
											for-pending
											submitted-state
											@selection:change="(n, e) => invoiceSelectionChange(n, e)"
											@open:details="(e) => showInvoiceDetails(e, false)"
											@approveInvoice="(e) => approveSelected(e)"
											@sendInvoiceForApproval="(e) => sendForApproval(e)"
										/>
									</div>
								</b-tab>
								<b-tab lazy :title="FormMSG(7, 'Approved')">
									<div class="pr-1 pl-1 w-100">
										<invoices-data-view
											ref="approved-data-view"
											:can-approve="canSeeApprove"
											for-approved
											submitted-state
											@selection:change="(n, e) => invoiceSelectionChange(n, e)"
											@open:details="(e) => showInvoiceDetails(e, false)"
											@approveInvoice="(e) => approveSelected(e)"
											@sendInvoiceForApproval="(e) => sendForApproval(e)"
										/>
									</div>
								</b-tab>
							</b-tabs>
						</b-card>
					</div>
				</b-row>
			</div>
		</div>
		<dialog-container
			ref="dragAndDropDialog"
			:show="openDragAndDropDialog"
			:dialog-title="dragAndDropDialogInfo.title"
			:btn-close-label="dragAndDropDialogInfo.closeLabel"
			:btn-save-label="dragAndDropDialogInfo.saveLabel"
			size="lg"
			:centered="true"
			:enable-next-or-save="ableToSwitchFinalForm"
			:disable-canel-or-close="filesUploadInProgress"
			@close="closeDragAndDropDialog"
			@nextOrSave="nextOrSaveProcess"
		>
			<template #content>
				<div :class="{ 'mt-4 mx-3': maxMobileScreenWidth, 'mx-5': !maxMobileScreenWidth }">
					<div class="d-flex" :class="maxMobileScreenWidth ? 'flex-column justify-content-start' : 'flex-row justify-content-between'">
						<div class="col-12 col-sm-6 col-md-6" :class="maxMobileScreenWidth ? 'p-0' : 'p-2 '">
							<b-form-checkbox v-model="isWithDetails" size="lg" plain>{{ FormMSG(53, 'with details') }}</b-form-checkbox>
						</div>
						<div class="d-flex">
							<b-form-group v-slot="{ ariaDescribedby }">
								<b-form-radio-group
									v-model="isForSingle"
									class="text-right my-2"
									name="radio-inline"
									plain
									:options="invoiceModeOptions"
									:aria-describedby="ariaDescribedby"
									:disabled="filesUploadInProgress"
									@change="preventChoiceIfInvoicesNotEmpty"
								/>
							</b-form-group>
						</div>
					</div>
					<files-uploader
						:disable-type-change="filesUploadInProgress"
						:files-list="currentProcessFiles"
						:for-single="isForSingle"
						@add="(e) => onAddFiles(e, !canAccessBudget)"
						@remove="onRemoveFiles"
					/>
				</div>
			</template>
		</dialog-container>
		<dialog-container
			ref="multipleInvoiceDialog"
			:show="openMultipleInvoiceDialog"
			:dialog-title="multipleInvoiceDialogInfo.title"
			:btn-close-label="multipleInvoiceDialogInfo.closeLabel"
			:btn-save-label="multipleInvoiceDialogInfo.saveLabel"
			:show-save-button="false"
			size="xxl"
			:centered="false"
			@close="closeMultipleInvoice"
		>
			<template #content>
				<invoices-data-view
					v-if="openMultipleInvoiceDialog"
					ref="multiple-data-view"
					for-multiple-invoice
					:loading-review-mode="isReviewModeLoading"
					:can-approve="canSeeApprove"
					:invoice-list="invoices"
					submitted-state
					@selection:change="(n, e) => invoiceSelectionChange(n, e)"
					@open:details="(e) => showInvoiceDetails(e, (canEdit = true), (isNewProcess = true), (isForSingle = false), (reviewMode = false))"
					@open:review-mode="(e) => openReviewMode(e, (newProcess = true))"
					@row:delete="removeInvoicesFromDataView"
				/>
			</template>
		</dialog-container>
		<dialog-invoice-details
			ref="dialogInvoiceDetails"
			:invoice="currentInvoice"
			:is-new-creation="isNewProcess"
			:total-invoices-to-review="invoicesToReview.length"
			:index-to-review="currentIndexToReview"
			use-script-expense-report-invoice
			@dialog-invoice:close="handleFinsihCloseOrCancelProcess('close')"
			@dialog-invoice:prev="onPrevOrNext(false)"
			@dialog-invoice:next="onPrevOrNext(true)"
		>
			<template #form-title>{{ finalFormDialogInfo.title }}</template>
			<template #form-content>
				<invoices-form
					ref="invoiceFinalForm"
					:invoice-data="currentInvoice"
					:tax-options-invoice="taxOptions"
					:custom-flags-invoice="customFlags"
					:can-edit="canEditFinalForm"
					:all-invoice-categories="allCategories"
					:is-new-creation="isNewProcess"
					:initial-operation="initialItemsOperation"
					@enable:save="(e) => ((enableFinalSaveBtn = e), (enableApproveBtnInModal = e))"
					@enable:error="(e, n) => showErrorMessage(e, n)"
					@delete:item="(item, index) => deleteLineItemsRow(item, index)"
					@submit:form="(invoice, itemsOperation) => nextOrSaveFromFinal(invoice, itemsOperation)"
					@file:uploaded="(file) => uploadFile(file)"
					@file:remove="(file) => removeAdditionalInvoiceFile(file)"
				/>
			</template>
			<template #form-error-message>{{ finalFormDialogError }}</template>
			<template #form-close-label>{{ isSavedOnce ? dialogMsg.btnClose : finalFormDialogInfo.closeLabel }}</template>
			<template #form-footer-action-left-btn>
				<b-button v-if="showApprovalsBtnInModal" size="md" variant="custom-outline-danger" class="w-138-px mr-3" @click="rejectInvoice()">
					<component :is="getLucideIcon('X')" :size="16" :stroke-width="3" class="mr-2" />{{ FormMSG(59, 'Reject') }}
				</b-button>
			</template>
			<template #form-footer-action-right-btn>
				<b-button v-if="canEditFinalForm" size="md" variant="primary" class="w-138-px mr-3" :disabled="!enableFinalSaveBtn" @click="saveInvoice">
					<div style="gap: 7px" class="d-flex align-items-center justify-content-center">
						<b-spinner v-show="isSaving" small /><span>{{ finalFormDialogInfo.saveLabel }}</span>
					</div>
				</b-button>
				<b-button
					v-if="showApprovalsBtnInModal"
					size="md"
					variant="dark"
					class="w-138-px mr-3 text-light d-flex align-items-center justify-content-center btn-custom-blue-dark"
					:disabled="!enableApproveBtnInModal"
					@click="approveSelectedInModal(selectedInvoice)"
				>
					<component :is="getLucideIcon('Check')" :size="16" :stroke-width="1.5" class="mr-2" />{{ FormMSG(270, 'Approve') }}
				</b-button>
			</template>
		</dialog-invoice-details>
		<dialog-reject-invoice
			ref="rejectInvoice"
			:selected-rows="[currentInvoice]"
			:show-modal="openRejectInvoiceDialog"
			@close="closeRejectModal"
			@reject="invoiceRejected"
		/>
		<common-warning-on-delete
			ref="warningDialog"
			v-model="showDeleteWarning"
			:message="messageOnCancel"
			@dialog:cancel="showDeleteWarning = false"
			@dialog:confirm="handleFinsihCloseOrCancelProcess('cancel')"
		/>
		<duplicate-expenses-warning
			v-model="showInvoiceDuplicatedAlert"
			:ocr-message-error="invoiceDuplicatedAlertMsg"
			@expenses-warning:ok="showInvoiceDuplicatedAlert = false"
		/>
	</errorContainer>
</template>

<script>
// components
import InvoicesDataView from './components/InvoicesDataView';
import DialogContainer from './components/DialogContainer';
import FilesUploader from './components/FilesUploader';
import InvoicesForm from './components/InvoicesForm';
import CommonWarningOnDelete from '@/components/Dialogs/CommonWarningOnDeleteDialog';
import DialogInvoiceDetails from './components/DialogInvoiceDetails';
import DuplicateExpensesWarning from '@/components/Modals/DuplicateExpensesWarningModal';
import DialogRejectInvoice from './components/DialogRejectInvoice';
// mixin
import globalMixin from '@/mixins/global.mixin';
import languageMessages from '@/mixins/languageMessages';
import isSingleProjectMixin from '@/mixins/isSingleProject.mixin';
import activeTabMixin from '@/mixins/activeTab.mixin';
import invoicesToolsMixin from '@/mixins/invoicesTools.mixin';
import commonCategoriesMixin from '@/mixins/commonCategories.mixin';

// cruds
import { getValidations } from '../../cruds/validations.crud';

// store & utils
import { store } from '@/store';
import { isNil } from '~utils';
import _ from 'lodash';

export default {
	name: 'MyInvoices',
	components: {
		InvoicesDataView,
		DialogContainer,
		FilesUploader,
		InvoicesForm,
		CommonWarningOnDelete,
		DialogInvoiceDetails,
		DuplicateExpensesWarning,
		DialogRejectInvoice
	},
	mixins: [languageMessages, isSingleProjectMixin, globalMixin, activeTabMixin, invoicesToolsMixin, commonCategoriesMixin],
	data() {
		return {
			// for tabs display
			currentValidationLevel: 0,
			// for modal dialog
			openRejectInvoiceDialog: false,
			enableApproveBtnInModal: true
		};
	},
	computed: {
		confirmModalTxt() {
			const _t = this.FormMSG;
			return {
				title: _t(302, 'Please Confirm'),
				okTitle: _t(300, 'YES'),
				cancelTitle: _t(301, 'NO')
			};
		},
		canAccessBudget() {
			return store.canViewGlobalBudget();
		},
		showApprovalsBtn() {
			return this.canSeeApprove && this.tabs.activeTabs === 0;
		},
		showApprovalsBtnInModal() {
			return this.canSeeApprove && this.tabs.activeTabs === 0 && !this.isNewProcess;
		},
		showSendValidationBtn() {
			return (this.canSeeApprove && this.tabs.activeTabs === 1) || (!this.canSeeApprove && this.tabs.activeTabs === 0);
		},
		ableToSwitchFinalForm() {
			if (this.invoices.length > 0) {
				return this.isStepToFinalFormFulfilled();
			} else return false;
		}
	},
	async created() {
		await this.getRoleValidation();
	},
	methods: {
		rejectInvoice() {
			const txt = this.FormMSG(303, 'Please confirm that you want to reject this invoice');
			this.$bvModal
				.msgBoxConfirm(txt, {
					title: this.confirmModalTxt.title,
					size: 'sm',
					buttonSize: 'sm',
					okVariant: 'danger',
					okTitle: this.confirmModalTxt.okTitle,
					cancelTitle: this.confirmModalTxt.cancelTitle,
					footerClass: 'p-2',
					hideHeaderClose: false,
					centered: false
				})
				.then(async (value) => {
					if (!value) return;
					this.openRejectInvoiceDialog = true;
				});
		},
		closeRejectModal() {
			this.openRejectInvoiceDialog = false;
		},
		async invoiceRejected() {
			const loadingAction = async () => {
				this.closeRejectModal();
				this.$refs.approveDataView.initData();
				if (this.$refs.dialogInvoiceDetails.isReviewMode && this.invoicesToReview.length > 1) {
					await this.refreshReviewModeOnRejectOrApprove();
				} else {
					await this.handleFinsihCloseOrCancelProcess('reject');
				}
			};
			await this.showLoader(loadingAction, 'default-container', false);
		},
		async approveSelectedInModal(invoice) {
			const loadingAction = async () => {
				await this.approveSelected([invoice]);
				if (this.$refs.dialogInvoiceDetails.isReviewMode && this.invoicesToReview.length > 1) {
					await this.refreshReviewModeOnRejectOrApprove();
				} else {
					await this.handleFinsihCloseOrCancelProcess('approve');
				}
			};
			await this.showLoader(loadingAction, 'default-container', false);
		},
		async getRoleValidation() {
			const validations = await getValidations(0);
			const userId = store.userID();

			// check if the departmentID in the store has value.
			let departmentId = null;
			if (store.state.myProfile && store.state.myProfile.department) {
				departmentId = store.state.myProfile.department;
			} else {
				// if not fill it.
				await store.setProjectAction();
				departmentId = store.state.myProfile ? store.state.myProfile.department : 0;
			}

			let haveValidationAllDepartment = null;
			let haveValidationSingleDepartment = null;

			if (validations) {
				for (let i = 0; i < validations.length; i++) {
					const element = validations[i];
					if (parseInt(element.department, 10) === -1 && parseInt(element.user.id, 10) === userId && element.type === 0) {
						haveValidationAllDepartment = element;
					} else if (parseInt(element.user.id, 10) === userId && parseInt(element.department, 10) === departmentId && element.type === 0) {
						haveValidationSingleDepartment = element;
					}
				}
			}
			// console.log(haveValidationAllDepartment);
			if (!isNil(haveValidationSingleDepartment) && !isNil(haveValidationAllDepartment)) {
				if (haveValidationSingleDepartment.validationLevelExp > haveValidationAllDepartment.validationLevelExp) {
					this.currentValidationLevel = haveValidationSingleDepartment.validationLevelExp;
				} else {
					this.currentValidationLevel = haveValidationAllDepartment.validationLevelExp;
				}
			} else {
				if (!isNil(haveValidationSingleDepartment)) {
					this.currentValidationLevel = haveValidationSingleDepartment.validationLevelExp;
				} else if (!isNil(haveValidationAllDepartment)) {
					this.currentValidationLevel = haveValidationAllDepartment.validationLevelExp;
				}
			}

			if ((this.currentValidationLevel === 2 || this.currentValidationLevel === 1) && !this.isFilmSingle) {
				this.canSeeApprove = true;
			} else {
				this.canSeeApprove = false;
			}
		},
		async openReviewModeFromIndex(invoices, index, newProcess) {
			// method preprareDetailsPayload is from invoicesToolsMixin
			this.invoicesToReview = _.cloneDeep(invoices);
			this.currentIndexToReview = index;
			const indexedInvoice = await this.preprareDetailsPayload(invoices[index]);
			this.showInvoiceDetails(indexedInvoice, this.canEditInvoice, newProcess, false, true);
		},
		async refreshReviewModeOnRejectOrApprove(newProcess = false) {
			// method preprareDetailsPayload is from invoicesToolsMixin
			this.invoicesToReview.splice(this.currentIndexToReview, 1);
			if (this.invoicesToReview.length < this.currentIndexToReview + 1) this.currentIndexToReview--;
			const nextInvoiceToReview = await this.preprareDetailsPayload(this.invoicesToReview[this.currentIndexToReview]);
			this.showInvoiceDetails(nextInvoiceToReview, this.canEditInvoice, newProcess, false, true);
		},
		showInvoiceDetailsToApprove(payload, index = null) {
			if (this.$screen.width > 992) {
				this.openReviewModeFromIndex(payload, index, false);
			} else {
				this.showInvoiceDetails(payload, this.canEditInvoice);
			}
		}
	}
};
</script>

<style>
.no-padding {
	padding: 0 !important;
}
</style>
